import { useEffect, useState, useMemo } from 'react';
import { Modal, Input, message as antdMessage, Col, Row, Button } from 'antd';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Form, Select } from 'formik-antd';
import PropTypes from 'prop-types';
import { commonCreate, commonUpdate, commonSearch } from 'store/commonSlice';
import { getClientList } from 'store/clientSlice';

const validationSchema = yup.object().shape({
	group_name: yup.string().required('Please enter a group name'),
	client_id: yup.string().required('Please select a client'),
	group_id: yup.string().required('Please select a WhatsApp group'),
	update_frequency: yup.number().required('Please select a frequency').positive().integer(),
});

export default function WhatsAppGroupSelector({
	modalOpen,
	setOpen,
	formData = null,
	refresh,
	resetRecord,
}) {
	const [groupOptions, setGroupOptions] = useState([]);
	const [groupLoading, setGroupLoading] = useState(false);
	const [groupSelectDisabled, setGroupSelectDisabled] = useState(true);
	const [clientsData, setClientsData] = useState([]);
	const dispatch = useDispatch();

	const isEdit = !!formData;

	const initialValues = useMemo(
		() => ({
			group_name: formData?.group_name || '',
			client_id: formData?.client_id || '',
			group_id: formData?.group_id || '',
			update_frequency: formData?.update_frequency || '',
		}),
		[formData]
	);

	useEffect(() => {
		if (modalOpen && isEdit && formData) {
			setClientsData([]);
		}
	}, [modalOpen, formData, isEdit]);
	useEffect(() => {
		if (!modalOpen) return;
		setGroupLoading(true);
		setGroupSelectDisabled(true);

		dispatch(
			getClientList({
				search_type: 'client',
				filter_by: {},
			})
		).then((res) => {
			const data = res?.data?.map((item) => ({
				value: item?.id,
				label: item?.client_info?.client_name,
			}));
			setClientsData(data);
		});
		dispatch(commonSearch({ search_type: 'whatsapp_wa_group' }))
			.then((res) => {
				setGroupOptions(res?.rows?.groups);
				setGroupLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setGroupLoading(false);
			})
			.finally(() => {
				setGroupLoading(false);
				setGroupSelectDisabled(false);
			});
	}, [modalOpen]);

	const handleClose = (handleReset) => {
		handleReset();
		resetRecord();
		setOpen(false);
	};

	const handleSubmit = async (values, { setSubmitting }) => {
		try {
			const payload = {
				...values,
				id: formData?.id,
			};
			if (formData) {
				dispatch(commonUpdate({ update_type: 'whatsapp_group', ...payload })).then(() => {
					antdMessage.success('Group Updated Successfully');
					setOpen(false);
					refresh(true);
					resetRecord();
				});
			} else {
				dispatch(commonCreate({ create_type: 'whatsapp_group', formdata: payload })).then(
					() => {
						antdMessage.success(isEdit ? 'Group updated' : 'Group created');
						setOpen(false);
						refresh(true);
						resetRecord();
					}
				);
			}
		} catch (error) {
			antdMessage.error(`Failed to ${isEdit ? 'update' : 'add'} group`);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, setFieldValue, handleChange, handleBlur, handleReset }) => (
				<Modal
					title={isEdit ? 'Edit WhatsApp Group' : 'Add WhatsApp Group'}
					open={modalOpen}
					onCancel={() => handleClose(handleReset)}
					footer={null}
					okText={isEdit ? 'Update' : 'Add'}
					destroyOnClose
					maskClosable={false}
				>
					<Form layout="vertical">
						<Row gutter={16} justify="space-between">
							<Col lg={24}>
								<Form.Item
									name="group_name"
									label="Group Label"
									validate={validationSchema.group_name}
								>
									<Input
										name="group_name"
										value={values.group_name}
										onChange={handleChange}
										onBlur={handleBlur}
										placeholder="e.g. Licious Temperature Tracker"
									/>
								</Form.Item>
							</Col>

							<Col lg={24}>
								<Form.Item name="client_id" label="Client Name">
									<Select
										name="client_id"
										value={values.client_id}
										onChange={(value) => setFieldValue('client_id', value)}
										placeholder="Select client"
										options={clientsData}
										showSearch
										filterOption={(input, option) =>
											option.label
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={16} justify="space-between">
							<Col lg={24}>
								<Form.Item name="group_id" label="WhatsApp Group">
									<Select
										name="group_id"
										value={values.group_id}
										onChange={(value) => setFieldValue('group_id', value)}
										placeholder={
											groupSelectDisabled
												? 'Loading WhatsApp groups...'
												: 'Select WhatsApp Group'
										}
										disabled={groupSelectDisabled || isEdit}
										loading={groupLoading}
										showSearch
										filterOption={(input, option) =>
											(option?.label ?? '')
												.toLowerCase()
												.includes(input.toLowerCase())
										}
										options={groupOptions.map((g) => ({
											label: g.subject || g.id,
											value: g.id,
										}))}
									/>
								</Form.Item>
							</Col>

							<Col lg={24}>
								<Form.Item
									name="update_frequency"
									label="Update Frequency (in minutes)"
								>
									<Select
										name="update_frequency"
										value={values.update_frequency}
										onChange={(value) =>
											setFieldValue('update_frequency', value)
										}
										placeholder="Select frequency"
										options={[1, 5, 10, 20, 30, 40, 50, 60].map((min) => ({
											label: `Every ${min} min`,
											value: min,
										}))}
									/>
								</Form.Item>
							</Col>
						</Row>
						<Row justify="end">
							<Button htmlType="submit" block type="primary">
								Submit
							</Button>
						</Row>
					</Form>
				</Modal>
			)}
		</Formik>
	);
}

WhatsAppGroupSelector.propTypes = {
	modalOpen: PropTypes.bool,
	setOpen: PropTypes.func,
	formData: PropTypes.object,
	refresh: PropTypes.func,
	resetRecord: PropTypes.func,
};
